import { useState } from 'react';
import { Link, useLocation, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiClientError, api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { useCan } from '../../lib/session';
import { ErrorLine, ErrorPanel } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';
import { Alert, Badge, Button, Card, ConfirmButton, LinkButton, PageHeader, SelectField, Spinner, TextField } from '../../components/ui';
import { formatDate } from './dates';
import { PLAT, Plan, PlanArray, Subscription, SubscriptionCancel, SubscriptionChangePlan, SubscriptionCreate, SubscriptionUpdate, keys, subStatusLabel, subStatusTone } from './api';
import { Money, MoneyField } from './parts';

type Period = 'monthly' | 'annual';
const today = () => new Date().toISOString().slice(0, 10);

function PlanSelect({ plans, value, onChange, label }: { plans: Plan[]; value: string; onChange: (id: string) => void; label: string }) {
  return (
    <SelectField label={label} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{t('billingUi.sub.choosePlan')}</option>
      {plans
        .filter((p) => p.active)
        .map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
    </SelectField>
  );
}

function PeriodSelect({ value, onChange }: { value: Period; onChange: (p: Period) => void }) {
  return (
    <SelectField label={t('billingUi.sub.period')} value={value} onChange={(e) => onChange(e.target.value as Period)}>
      <option value="monthly">{t('billingUi.period.monthly')}</option>
      <option value="annual">{t('billingUi.period.annual')}</option>
    </SelectField>
  );
}

function CreateCard({ tenantId, plans, onDone }: { tenantId: string; plans: Plan[]; onDone: () => void }) {
  const [planId, setPlanId] = useState('');
  const [period, setPeriod] = useState<Period>('monthly');
  const [startDate, setStartDate] = useState(today());
  const [trial, setTrial] = useState('');
  const [discount, setDiscount] = useState('0');
  const [note, setNote] = useState('');
  const toast = useToast();
  const create = useMutation({
    mutationFn: () =>
      api.put(`${PLAT}/tenants/${tenantId}/subscription`, {
        body: SubscriptionCreate.parse({ planId, period, startDate, trialDays: trial === '' ? undefined : Number(trial), discountPercent: Number(discount) || 0, discountNote: note }),
        schema: Subscription,
      }),
    onSuccess: () => {
      toast(t('billingUi.sub.created'));
      onDone();
    },
  });
  return (
    <Card
      title={t('billingUi.sub.createTitle')}
      description={t('billingUi.sub.createHelp')}
      footer={
        <Button variant="primary" disabled={!planId || create.isPending} onClick={() => create.mutate()}>
          {t('billingUi.sub.create')}
        </Button>
      }
    >
      <ErrorLine error={create.error} />
      <div className="grid gap-3 sm:grid-cols-2">
        <PlanSelect plans={plans} value={planId} onChange={setPlanId} label={t('billingUi.col.plan')} />
        <PeriodSelect value={period} onChange={setPeriod} />
        <TextField label={t('billingUi.sub.startDate')} type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        <TextField label={t('billingUi.sub.trialDays')} hint={t('billingUi.sub.trialHint')} type="number" min={0} max={365} value={trial} onChange={(e) => setTrial(e.target.value)} />
        <TextField label={t('billingUi.sub.discount')} type="number" min={0} max={100} value={discount} onChange={(e) => setDiscount(e.target.value)} />
        <TextField label={t('billingUi.sub.discountNote')} value={note} maxLength={200} onChange={(e) => setNote(e.target.value)} />
      </div>
    </Card>
  );
}

function SummaryCard({ s }: { s: Subscription }) {
  const row = (label: string, value: React.ReactNode) => (
    <div className="grid grid-cols-3 gap-2 py-1.5 text-sm">
      <dt className="text-slate-600">{label}</dt>
      <dd className="col-span-2">{value}</dd>
    </div>
  );
  return (
    <Card title={t('billingUi.sub.summary')}>
      <dl className="divide-y divide-slate-100">
        {row(t('billingUi.col.plan'), s.planName)}
        {row(t('common.status'), <Badge tone={subStatusTone(s.status)}>{subStatusLabel(s.status)}</Badge>)}
        {row(t('billingUi.sub.period'), t(`billingUi.period.${s.period}`))}
        {row(t('billingUi.sub.currentPeriod'), t('billingUi.sub.range', { from: formatDate(s.currentPeriodStart), to: formatDate(s.currentPeriodEnd) }))}
        {s.trialEndsOn ? row(t('billingUi.sub.trialEnds'), formatDate(s.trialEndsOn)) : null}
        {row(t('billingUi.col.nextInvoice'), s.nextInvoiceOn ? formatDate(s.nextInvoiceOn) : t('billingUi.none'))}
        {s.overdueBalance > 0 ? row(t('billingUi.col.overdue'), <Money pence={s.overdueBalance} />) : null}
        {s.restriction !== 'none' ? row(t('billingUi.sub.restriction'), t(`billingUi.restriction.${s.restriction}`)) : null}
        {s.pendingPlanId ? row(t('billingUi.sub.pending'), t('billingUi.sub.pendingText', { period: t(`billingUi.period.${s.pendingPeriod ?? s.period}`) })) : null}
        {s.cancelledAt ? row(t('billingUi.sub.cancelled'), formatDate(s.cancelledAt.slice(0, 10))) : null}
      </dl>
    </Card>
  );
}

function EditCard({ s, tenantId }: { s: Subscription; tenantId: string }) {
  const [discount, setDiscount] = useState(String(s.discountPercent));
  const [note, setNote] = useState(s.discountNote);
  const [addOns, setAddOns] = useState(s.addOns.map((a) => ({ ...a })));
  const qc = useQueryClient();
  const toast = useToast();
  const save = useMutation({
    mutationFn: () =>
      api.patch(`${PLAT}/tenants/${tenantId}/subscription`, {
        body: SubscriptionUpdate.parse({ discountPercent: Number(discount) || 0, discountNote: note, addOns: addOns.filter((a) => a.moduleKey.trim()) }),
        schema: Subscription,
      }),
    onSuccess: async () => {
      toast(t('billingUi.sub.saved'));
      await qc.invalidateQueries({ queryKey: keys.sub(tenantId) });
    },
  });
  return (
    <Card
      title={t('billingUi.sub.editTitle')}
      description={t('billingUi.sub.editHelp')}
      footer={
        <Button variant="primary" disabled={save.isPending} onClick={() => save.mutate()}>
          {t('common.save')}
        </Button>
      }
    >
      <ErrorLine error={save.error} />
      <div className="grid gap-3 sm:grid-cols-2">
        <TextField label={t('billingUi.sub.discount')} type="number" min={0} max={100} value={discount} onChange={(e) => setDiscount(e.target.value)} />
        <TextField label={t('billingUi.sub.discountNote')} value={note} maxLength={200} onChange={(e) => setNote(e.target.value)} />
      </div>
      <div className="mt-4 space-y-2">
        <h3 className="text-sm font-medium text-slate-800">{t('billingUi.sub.addOns')}</h3>
        {addOns.map((a, i) => (
          <div key={i} className="grid items-end gap-3 sm:grid-cols-[1fr_1fr_auto]">
            <TextField label={t('billingUi.plans.addOnKey')} value={a.moduleKey} onChange={(e) => setAddOns(addOns.map((x, j) => (j === i ? { ...x, moduleKey: e.target.value } : x)))} />
            <MoneyField label={t('billingUi.plans.addOnPrice')} value={a.monthlyPrice} onValue={(p) => setAddOns(addOns.map((x, j) => (j === i ? { ...x, monthlyPrice: p ?? 0 } : x)))} />
            <Button onClick={() => setAddOns(addOns.filter((_, j) => j !== i))} aria-label={t('billingUi.plans.removeAddOn', { n: i + 1 })}>
              {t('billingUi.remove')}
            </Button>
          </div>
        ))}
        <Button onClick={() => setAddOns([...addOns, { moduleKey: '', monthlyPrice: 0 }])}>{t('billingUi.plans.addAddOn')}</Button>
      </div>
    </Card>
  );
}

function ChangePlanCard({ s, plans, tenantId }: { s: Subscription; plans: Plan[]; tenantId: string }) {
  const [planId, setPlanId] = useState('');
  const [period, setPeriod] = useState<Period>(s.period);
  const [effective, setEffective] = useState<'now' | 'period_end'>('period_end');
  const qc = useQueryClient();
  const toast = useToast();
  const change = useMutation({
    mutationFn: () => api.post(`${PLAT}/tenants/${tenantId}/subscription/change-plan`, { body: SubscriptionChangePlan.parse({ planId, period, effective }), schema: Subscription }),
    onSuccess: async () => {
      toast(t('billingUi.sub.planChanged'));
      setPlanId('');
      await qc.invalidateQueries({ queryKey: keys.sub(tenantId) });
    },
  });
  return (
    <Card
      title={t('billingUi.sub.changeTitle')}
      description={t('billingUi.sub.changeHelp')}
      footer={
        <Button variant="primary" disabled={!planId || change.isPending} onClick={() => change.mutate()}>
          {t('billingUi.sub.change')}
        </Button>
      }
    >
      <ErrorLine error={change.error} />
      <div className="grid gap-3 sm:grid-cols-3">
        <PlanSelect plans={plans} value={planId} onChange={setPlanId} label={t('billingUi.sub.newPlan')} />
        <PeriodSelect value={period} onChange={setPeriod} />
        <SelectField label={t('billingUi.sub.effective')} value={effective} onChange={(e) => setEffective(e.target.value as 'now' | 'period_end')}>
          <option value="period_end">{t('billingUi.sub.atPeriodEnd')}</option>
          <option value="now">{t('billingUi.sub.now')}</option>
        </SelectField>
      </div>
    </Card>
  );
}

function CancelCard({ tenantId }: { tenantId: string }) {
  const [effective, setEffective] = useState<'now' | 'period_end'>('period_end');
  const [reason, setReason] = useState('');
  const qc = useQueryClient();
  const toast = useToast();
  const cancel = useMutation({
    mutationFn: () => api.post(`${PLAT}/tenants/${tenantId}/subscription/cancel`, { body: SubscriptionCancel.parse({ effective, reason }), schema: Subscription }),
    onSuccess: async () => {
      toast(t('billingUi.sub.cancelDone'));
      await qc.invalidateQueries({ queryKey: keys.sub(tenantId) });
    },
  });
  return (
    <Card title={t('billingUi.sub.cancelTitle')} description={t('billingUi.sub.cancelHelp')}>
      <ErrorLine error={cancel.error} />
      <div className="mb-3 grid gap-3 sm:grid-cols-2">
        <SelectField label={t('billingUi.sub.effective')} value={effective} onChange={(e) => setEffective(e.target.value as 'now' | 'period_end')}>
          <option value="period_end">{t('billingUi.sub.atPeriodEnd')}</option>
          <option value="now">{t('billingUi.sub.now')}</option>
        </SelectField>
        <TextField label={t('billingUi.sub.reason')} value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} />
      </div>
      <ConfirmButton label={t('billingUi.sub.cancel')} confirmLabel={t('billingUi.sub.cancelConfirm')} pending={cancel.isPending} onConfirm={() => cancel.mutate()} />
    </Card>
  );
}

/** Platform: one customer's subscription (also linked from the tenant detail page). */
export function SubscriptionPage() {
  const { tenantId = '' } = useParams();
  const loc = useLocation();
  const name = (loc.state as { name?: string } | null)?.name ?? tenantId.slice(0, 8);
  const canManage = useCan('platform.billing_manage');
  const qc = useQueryClient();
  const sub = useQuery({
    queryKey: keys.sub(tenantId),
    queryFn: () => api.get(`${PLAT}/tenants/${tenantId}/subscription`, { schema: Subscription }),
  });
  const plans = useQuery({ queryKey: keys.plans, queryFn: () => api.get(`${PLAT}/plans`, { schema: PlanArray }) });
  const none = sub.error instanceof ApiClientError && sub.error.status === 404;
  const refresh = () => void qc.invalidateQueries({ queryKey: ['billing'] });
  return (
    <>
      <PageHeader
        title={t('billingUi.sub.title', { name })}
        description={t('billingUi.sub.description')}
        actions={
          <>
            <LinkButton to={`/platform/billing/invoices?tenantId=${tenantId}`}>{t('billingUi.tabs.invoices')}</LinkButton>
            <LinkButton to="/platform/billing">{t('billingUi.sub.back')}</LinkButton>
          </>
        }
      />
      {sub.isPending || plans.isPending ? (
        <Spinner />
      ) : plans.isError ? (
        <ErrorPanel error={plans.error} onRetry={() => void plans.refetch()} />
      ) : sub.isError && !none ? (
        <ErrorPanel error={sub.error} onRetry={() => void sub.refetch()} />
      ) : none ? (
        canManage ? (
          <CreateCard tenantId={tenantId} plans={plans.data} onDone={refresh} />
        ) : (
          <Alert kind="info">{t('billingUi.sub.none')}</Alert>
        )
      ) : (
        <div className="space-y-5">
          <SummaryCard s={sub.data!} />
          {canManage && sub.data!.status !== 'cancelled' ? (
            <>
              <EditCard key={sub.data!.updatedAt} s={sub.data!} tenantId={tenantId} />
              <ChangePlanCard s={sub.data!} plans={plans.data} tenantId={tenantId} />
              <CancelCard tenantId={tenantId} />
            </>
          ) : null}
          <p className="text-sm">
            <Link className="text-brand hover:underline" to={`/platform/billing/invoices?tenantId=${tenantId}`}>
              {t('billingUi.sub.seeInvoices')}
            </Link>
          </p>
        </div>
      )}
    </>
  );
}
